import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SecuritySettings {
  sessionTimeoutMinutes: number;
  maxLoginAttempts: number;
  lockoutDurationMinutes: number;
  minPasswordLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
}

const DEFAULT_SETTINGS: SecuritySettings = {
  sessionTimeoutMinutes: 480,
  maxLoginAttempts: 5,
  lockoutDurationMinutes: 15,
  minPasswordLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
};

export const useSecuritySettings = () => {
  const [settings, setSettings] = useState<SecuritySettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'security')
        .maybeSingle();

      if (error) {
        // Silently fall back to defaults when not authenticated or row missing
        return;
      }

      if (data?.value) {
        setSettings(data.value as unknown as SecuritySettings);
      }
    } catch (e) {
      console.error('Failed to load security settings:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const validatePassword = useCallback((password: string): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (password.length < settings.minPasswordLength) {
      errors.push(`Password must be at least ${settings.minPasswordLength} characters`);
    }
    if (settings.requireUppercase && !/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (settings.requireLowercase && !/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (settings.requireNumbers && !/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    if (settings.requireSpecialChars && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }

    return { valid: errors.length === 0, errors };
  }, [settings]);

  const getPasswordRequirements = useCallback(() => {
    const requirements: { label: string; check: (pw: string) => boolean }[] = [
      { label: `At least ${settings.minPasswordLength} characters`, check: (pw) => pw.length >= settings.minPasswordLength },
    ];

    if (settings.requireUppercase) {
      requirements.push({ label: 'One uppercase letter', check: (pw) => /[A-Z]/.test(pw) });
    }
    if (settings.requireLowercase) {
      requirements.push({ label: 'One lowercase letter', check: (pw) => /[a-z]/.test(pw) });
    }
    if (settings.requireNumbers) {
      requirements.push({ label: 'One number', check: (pw) => /[0-9]/.test(pw) });
    }
    if (settings.requireSpecialChars) {
      requirements.push({ label: 'One special character (!@#$...)', check: (pw) => /[!@#$%^&*(),.?":{}|<>]/.test(pw) });
    }

    return requirements;
  }, [settings]);

  return {
    settings,
    loading,
    validatePassword,
    getPasswordRequirements,
    refetch: fetchSettings,
  };
};
